import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useContext, useRef } from 'react';
import type { PersonDetailsProps } from './PersonDetails.data';
import styles from './PersonDetails.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './PersonDetails.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import { MARK_LINK, NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import PrimaryLink from '../../atoms/primary-link/PrimaryLink';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import Link from 'next/link';
import type { AhrefLink, ImageLink } from '../../../util/storyblok/types/global.types';
import type { SiteContextType } from '../../unlisted/SiteContext';
import { SiteContext } from '../../unlisted/SiteContext';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import Image from '../../atoms/image/Image';
import type { HeadingRef } from '../../atoms/heading/Heading.data';
import type { TextRef } from '../../atoms/text/Text.data';
import PersonDetailsInfo from './PersonDetailsInfo';

export default function PersonDetails({
  isVisible,
  className,
  blok,
  ref,
}: PersonDetailsProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const { imageModalRef } = useContext(SiteContext) as SiteContextType;
  const nameRef = useRef<HeadingRef>(null);
  const pronounRef = useRef<TextRef>(null);
  const labelRef = useRef<HeadingRef>(null);
  const jobTitleRef = useRef<TextRef>(null);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);
  useThemeScrollBlock(elementRef, blok.use_accent_color);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.personDetails, className)}
      {...sbEditable(blok)}
    >
      <div className={classNames(styles.flexContainer)}>
        <div className={classNames(styles.leftContainer)}>
          <div className={styles.imageContainer}>
            {blok.portrait?.filename && (
              <Image
                src={blok.portrait.filename}
                srcDesktop={blok.portrait.filename}
                alt={blok.portrait.alt}
              />
            )}
          </div>

          <div>
            {blok.name && (
              <Heading
                ref={nameRef}
                as="h5"
                size="heading-05"
                disableTriggerInOnScroll={false}
                transitionInType="slideUpFadeIn"
                transitionOutType="fadeOut"
                className={styles.name}
              >
                {blok.name}
              </Heading>
            )}

            {blok.pronouns && (
              <Text
                ref={pronounRef}
                as="p"
                className={styles.pronouns}
                size="body-standard"
                disableTriggerInOnScroll={false}
                transitionInType="slideUpFadeIn"
                transitionOutType="fadeOut"
              >
                {blok.pronouns}
              </Text>
            )}
          </div>

          <div className={classNames(styles.leftPersonsContainer)}>
            <div>
              {blok.job_title && (
                <div className={styles.functionContainer}>
                  <Heading
                    as="span"
                    size="label-small"
                    disableTriggerInOnScroll={false}
                    transitionInType="slideUpFadeIn"
                    transitionOutType="fadeOut"
                    className={styles.label}
                    ref={labelRef}
                  >
                    {blok.label_function}
                  </Heading>

                  <Text
                    ref={jobTitleRef}
                    as="p"
                    size="body-standard"
                    disableTriggerInOnScroll={false}
                    transitionInType="splitLineRotateIn"
                    transitionOutType="fadeOut"
                  >
                    {blok.job_title}
                  </Text>
                </div>
              )}

              {blok.contact?.[0] && <PersonDetailsInfo details={blok.contact[0]} />}
            </div>
          </div>

          {blok.socials_links?.length > 0 && (
            <div className={styles.socialLinksContainer}>
              <Heading
                as="h6"
                size="label-small"
                disableTriggerInOnScroll={false}
                transitionInType="slideUpFadeIn"
                transitionOutType="fadeOut"
                className={styles.socialsTitle}
              >
                {blok.socials_title}
              </Heading>

              {blok.socials_links.map((link) =>
                link.component === 'image_link' ? (
                  <PrimaryLink
                    key={link._uid}
                    textVariant="body-standard"
                    className={classNames(styles.social)}
                    disableTriggerInOnScroll={false}
                    transitionInType="splitLineRotateIn"
                    label={link.title}
                    onClick={() =>
                      imageModalRef?.current?.open({
                        image: (link as ImageLink)?.image,
                        title: link.title,
                      })
                    }
                  />
                ) : (
                  <PrimaryLink
                    key={link._uid}
                    textVariant="body-standard"
                    className={classNames(styles.social)}
                    link={{
                      href: (link as AhrefLink)?.link.url || (link as AhrefLink)?.link.cached_url,
                    }}
                    disableTriggerInOnScroll={false}
                    transitionInType="splitLineRotateIn"
                    label={link.title}
                    target={(link as AhrefLink)?.link.target}
                  />
                ),
              )}
            </div>
          )}
        </div>

        <div className={styles.textContainer}>
          {render(blok.description, {
            nodeResolvers: {
              [NODE_PARAGRAPH]: (children) => (
                <Text
                  className={styles.description}
                  as="p"
                  size="body-large"
                  transitionInType="splitCharsWordsRichText"
                  disableTriggerInOnScroll={false}
                >
                  {children}
                </Text>
              ),
            },
            markResolvers: {
              [MARK_LINK]: (children, props) => {
                if (props.linktype === 'story') {
                  return (
                    <Link href={`${props.href}#${props.anchor}`} passHref>
                      {}
                      {children}
                    </Link>
                  );
                }
                return <a {...props}>{children}</a>;
              },
            },
          })}
        </div>
      </div>
    </div>
  );
}
